import { useState, useCallback, useMemo } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import {
  Copy,
  Check,
  RefreshCw,
  Trash2,
  Type,
  Download,
  Languages
} from 'lucide-react'

// Text source data
const LATIN_WORDS = [
  'lorem','ipsum','dolor','sit','amet','consectetur','adipiscing','elit','sed','do',
  'eiusmod','tempor','incididunt','ut','labore','et','dolore','magna','aliqua','enim',
  'ad','minim','veniam','quis','nostrud','exercitation','ullamco','laboris','nisi','aliquip',
  'ex','ea','commodo','consequat','duis','aute','irure','in','reprehenderit','voluptate',
  'velit','esse','cillum','fugiat','nulla','pariatur','excepteur','sint','occaecat','cupidatat',
  'non','proident','sunt','culpa','qui','officia','deserunt','mollit','anim','id','est','laborum'
]

const CHINESE_WORDS = [
  '的','一','是','在','不','了','有','和','人','这','中','大','为','上','个','国','我',
  '以','要','他','时','来','用','们','生','到','作','地','于','出','就','分','对','成',
  '会','可','主','发','年','动','同','工','也','能','下','过','子','说','产','种','面',
  '而','方','后','多','定','行','学','法','所','民','得','经','十','三','之','进','着',
  '等','部','度','家','电','力','里','如','水','化','高','自','二','理','起','小','物',
  '现','实','加','量','都','两','体','制','机','当','使','点','从','业','本','去','把',
  '性','好','应','开','它','合','还','因','由','其','些','然','前','外','天','政','四',
  '日','那','社','义','事','平','形','相','全','表','间','样','与','关','各','新','线',
  '内','数','正','心','反','你','明','看','原','又','么','利','比','或','但','质','气',
  '第','向','道','命','此','变','条','只','没','结','解','问','意','建','月','公','无'
]

const BUSINESS_WORDS = [
  'leverage','synergy','stakeholder','deliverable','actionable','scalable','robust','streamline',
  'optimize','alignment','bandwidth','paradigm','ecosystem','disruption','innovation','sustainable',
  'agile','transformative','holistic','strategic','value','proposition','market','analysis',
  'growth','revenue','profit','margin','customer','experience','journey','engagement','retention',
  'acquisition','conversion','funnel','metrics','KPI','ROI','benchmark','best','practice',
  'collaboration','cross','functional','team','workforce','talent','culture','diversity',
  'inclusion','equity','performance','management','feedback','development','upskill','reskill'
]

const TECH_WORDS = [
  'algorithm','data','structure','function','variable','loop','condition','array','object',
  'class','method','interface','API','endpoint','request','response','JSON','XML','database',
  'query','index','cache','memory','thread','process','async','await','promise','callback',
  'event','listener','handler','component','module','library','framework','runtime','compiler',
  'interpreter','virtual','machine','container','docker','kubernetes','microservice','serverless',
  'cloud','compute','storage','network','protocol','packet','latency','bandwidth','throughput',
  'encryption','hash','token','authentication','authorization','session','cookie','middleware',
  'routing','controller','model','view','template','render','hydrate','state','props','hook'
]

type TextStyle = 'latin' | 'chinese' | 'business' | 'tech'

function getWords(style: TextStyle): string[] {
  switch (style) {
    case 'latin': return LATIN_WORDS
    case 'chinese': return CHINESE_WORDS
    case 'business': return BUSINESS_WORDS
    case 'tech': return TECH_WORDS
  }
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function generateSentence(words: string[], minWords: number, maxWords: number, style: TextStyle): string {
  const count = Math.floor(Math.random() * (maxWords - minWords + 1)) + minWords
  const sentence: string[] = []

  for (let i = 0; i < count; i++) {
    sentence.push(words[Math.floor(Math.random() * words.length)])
  }

  if (style === 'chinese') {
    return sentence.join('') + '。'
  }

  sentence[0] = capitalize(sentence[0])
  return sentence.join(' ') + '.'
}

function generateParagraph(style: TextStyle, sentences: number): string {
  const words = getWords(style)
  const minWords = style === 'chinese' ? 8 : 6
  const maxWords = style === 'chinese' ? 20 : 15
  const sents: string[] = []

  for (let i = 0; i < sentences; i++) {
    sents.push(generateSentence(words, minWords, maxWords, style))
  }

  return sents.join(style === 'chinese' ? '' : ' ')
}

function generateText(style: TextStyle, paragraphs: number, sentencesPerParagraph: number, startWithLorem: boolean): string {
  const paras: string[] = []

  for (let i = 0; i < paragraphs; i++) {
    let para = generateParagraph(style, sentencesPerParagraph)

    // First paragraph optionally starts with Lorem ipsum...
    if (i === 0 && startWithLorem && style === 'latin') {
      para = 'Lorem ipsum dolor sit amet, ' + para.toLowerCase().replace(/^lorem ipsum dolor sit amet,\s*/, '')
    }

    if (i === 0 && startWithLorem && style === 'chinese') {
      para = '乱数假文，' + para.replace(/^乱数假文[，,]?\s*/, '')
    }

    paras.push(para)
  }

  return paras.join('\n\n')
}

function countWords(text: string, style: TextStyle): number {
  if (style === 'chinese') {
    return text.replace(/[^\u4e00-\u9fff]/g, '').length
  }
  return text.trim().split(/\s+/).filter(w => w.length > 0).length
}

function countChars(text: string): number {
  return text.length
}

function countSentences(text: string): number {
  return text.split(/[.!?。！？]+/).filter(s => s.trim().length > 0).length
}

function App() {
  const { t } = useI18n()
  const [style, setStyle] = useState<TextStyle>('latin')
  const [paragraphs, setParagraphs] = useState(3)
  const [sentencesPerParagraph, setSentencesPerParagraph] = useState(5)
  const [startWithLorem, setStartWithLorem] = useState(true)
  const [wrapHtml, setWrapHtml] = useState(false)
  const [text, setText] = useState('')
  const [copied, setCopied] = useState(false)

  const generate = useCallback(() => {
    const generated = generateText(style, paragraphs, sentencesPerParagraph, startWithLorem)
    setText(generated)
  }, [style, paragraphs, sentencesPerParagraph, startWithLorem])

  const stats = useMemo(() => ({
    words: countWords(text, style),
    chars: countChars(text),
    sentences: countSentences(text),
    paragraphs: text.split('\n\n').filter(p => p.trim().length > 0).length,
  }), [text, style])

  const copyToClipboard = useCallback(async () => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }, [text])

  const downloadText = useCallback(() => {
    if (!text) return
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `lorem-ipsum-${paragraphs}p-${Date.now()}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [text, paragraphs])

  const clearText = useCallback(() => {
    setText('')
  }, [])

  const displayText = wrapHtml
    ? text.split('\n\n').map(p => `<p>${p}</p>`).join('\n')
    : text

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('lorem.title') || 'Lorem Ipsum Generator'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('lorem.subtitle') || 'Generate placeholder text for design, development, and content mockups. '}
          <span className="text-primary font-medium">{t('lorem.privacy') || '100% client-side'}</span>.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Settings Panel */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-4">
          {/* Style Selector */}
          <div>
            <label className="text-sm font-medium text-foreground flex items-center gap-1.5 mb-2">
              <Languages className="w-4 h-4 text-brand-primary" />
              {t('lorem.style') || 'Text Style'}
            </label>
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'latin', label: 'Latin', labelKey: 'styleLatin' },
                { key: 'chinese', label: 'Chinese', labelKey: 'styleChinese' },
                { key: 'business', label: 'Business', labelKey: 'styleBusiness' },
                { key: 'tech', label: 'Tech', labelKey: 'styleTech' },
              ] as const).map(({ key, label, labelKey }) => (
                <button
                  key={key}
                  onClick={() => setStyle(key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    style === key
                      ? 'bg-brand-primary text-white'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {t(`lorem.${labelKey}`) || label}
                </button>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Paragraphs */}
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">{t('lorem.paragraphs') || 'Paragraphs'}</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={1}
                  max={20}
                  value={paragraphs}
                  onChange={e => setParagraphs(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium text-foreground w-8 text-right">{paragraphs}</span>
              </div>
            </div>

            {/* Sentences per paragraph */}
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">{t('lorem.sentences') || 'Sentences / Paragraph'}</label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={1}
                  max={20}
                  value={sentencesPerParagraph}
                  onChange={e => setSentencesPerParagraph(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium text-foreground w-8 text-right">{sentencesPerParagraph}</span>
              </div>
            </div>

            {/* Start with Lorem */}
            <div className="flex items-center gap-2 sm:pt-5">
              <input
                id="start-lorem"
                type="checkbox"
                checked={startWithLorem}
                onChange={e => setStartWithLorem(e.target.checked)}
                className="w-4 h-4 rounded border-border text-brand-primary focus:ring-brand-primary"
              />
              <label htmlFor="start-lorem" className="text-sm text-foreground">{t('lorem.startWithLorem') || 'Start with Lorem Ipsum'}</label>
            </div>

            {/* Wrap in HTML */}
            <div className="flex items-center gap-2 sm:pt-5">
              <input
                id="wrap-html"
                type="checkbox"
                checked={wrapHtml}
                onChange={e => setWrapHtml(e.target.checked)}
                className="w-4 h-4 rounded border-border text-brand-primary focus:ring-brand-primary"
              />
              <label htmlFor="wrap-html" className="text-sm text-foreground">{t('lorem.wrapHtml') || 'Wrap in HTML <p>'}</label>
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={generate}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              {t('lorem.generate') || 'Generate'}
            </button>
            <button
              onClick={copyToClipboard}
              disabled={!text}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-background border border-border text-foreground rounded-lg hover:bg-muted transition-colors text-sm font-medium disabled:opacity-50"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              {copied ? (t('actions.copied') || 'Copied!') : (t('actions.copy') || 'Copy')}
            </button>
            <button
              onClick={downloadText}
              disabled={!text}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-background border border-border text-foreground rounded-lg hover:bg-muted transition-colors text-sm font-medium disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {t('actions.download') || 'Download'}
            </button>
            <button
              onClick={clearText}
              disabled={!text}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-background border border-border text-foreground rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400 transition-colors text-sm font-medium disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {t('actions.clear') || 'Clear'}
            </button>
          </div>
        </div>

        {/* Output + Stats */}
        {text && (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-surface rounded-xl shadow-sm border border-border p-3 text-center">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('lorem.statWords') || 'Words'}</div>
                <div className="text-2xl font-bold text-foreground">{stats.words}</div>
              </div>
              <div className="bg-surface rounded-xl shadow-sm border border-border p-3 text-center">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('lorem.statChars') || 'Characters'}</div>
                <div className="text-2xl font-bold text-foreground">{stats.chars}</div>
              </div>
              <div className="bg-surface rounded-xl shadow-sm border border-border p-3 text-center">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('lorem.statSentences') || 'Sentences'}</div>
                <div className="text-2xl font-bold text-foreground">{stats.sentences}</div>
              </div>
              <div className="bg-surface rounded-xl shadow-sm border border-border p-3 text-center">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('lorem.statParagraphs') || 'Paragraphs'}</div>
                <div className="text-2xl font-bold text-foreground">{stats.paragraphs}</div>
              </div>
            </div>

            {/* Text Output */}
            <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
              <pre className="whitespace-pre-wrap text-sm text-foreground font-mono leading-relaxed max-h-[500px] overflow-y-auto">
                {displayText}
              </pre>
            </div>
          </div>
        )}

        {!text && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Type className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('lorem.emptyHint') || 'Configure settings above and click Generate to create placeholder text'}
            </p>
          </div>
        )}

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            {t('security.note')}
          </p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
